import { motion } from "framer-motion";
import BillboardLogo from "@/components/shared/BillboardLogo";

export default function BillboardHero() {
  return (
    <section
      data-testid="home-billboard-hero"
      className="relative py-14 md:py-16 lg:py-20 border-t border-white/5 bg-black overflow-hidden"
    >
      {/* Ambient wash — pushed brighter for more presence */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 22% 42%, rgba(255,255,255,0.12) 0%, transparent 60%), radial-gradient(circle at 82% 62%, rgba(255,255,255,0.08) 0%, transparent 60%)",
        }}
      />
      {/* Subtle halftone dots */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1.5px)",
          backgroundSize: "16px 16px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — the number */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5 text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-8">
              <span className="w-6 h-px bg-zinc-600" />
              Chart Record
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9 }}
              className="relative"
            >
              {/* Multi-layer white glow — softer, wider, brighter */}
              <div
                aria-hidden
                className="absolute -inset-16 -z-10 pointer-events-none blur-[90px] opacity-80"
                style={{
                  background:
                    "radial-gradient(circle at 40% 45%, rgba(255,255,255,0.34) 0%, transparent 65%)",
                }}
              />
              <div
                aria-hidden
                className="absolute -inset-6 -z-10 pointer-events-none blur-3xl opacity-60"
                style={{
                  background:
                    "radial-gradient(circle at 40% 50%, rgba(255,255,255,0.4) 0%, transparent 58%)",
                }}
              />
              <span
                className="font-display font-black text-[8.5rem] md:text-[10.5rem] lg:text-[12.5rem] tracking-tighter leading-[0.82] block"
                style={{
                  background:
                    "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 55%, rgba(255,255,255,0.55) 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                20+
              </span>
              <div className="mt-4 flex items-center gap-3">
                <span className="h-px w-16 bg-white/60" />
                <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
                  Songs charted
                </span>
              </div>
            </motion.div>

            {/* Billboard pill — pronounced white glow */}
            <div className="relative mt-7 inline-block">
              {/* Soft radial glow behind the pill */}
              <div
                aria-hidden
                className="absolute -inset-6 -z-10 pointer-events-none blur-2xl opacity-60"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 65%)",
                }}
              />
              <div
                className="inline-flex items-center gap-3 border border-white/20 rounded-full px-5 py-3 bg-white/[0.04]"
                style={{
                  boxShadow:
                    "0 0 32px rgba(255,255,255,0.18), 0 0 8px rgba(255,255,255,0.12), inset 0 0 0 1px rgba(255,255,255,0.06)",
                }}
              >
                <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-400">On</span>
                <BillboardLogo height={16} />
                <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-400">Hot 100</span>
              </div>
            </div>
          </div>

          {/* Right — copy + chart-tier proof */}
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9, delay: 0.05 }}
              className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-balance leading-[1.02]"
            >
              Songs we've helped push
              <span className="italic font-light"> onto the Billboard Hot 100.</span>
            </motion.h2>

            <p className="mt-5 max-w-xl text-zinc-400 leading-relaxed">
              Chart movement is the only KPI that matters, and it's the one every campaign
              is pointed at. The specific artists, labels, and campaign breakdowns are held
              under NDA, available on a strategy call.
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl" data-testid="billboard-chart-tiers">
              {[
                { label: "Hot 100", tag: "Multiple placements" },
                { label: "Hot Country Songs", tag: "Chart debuts" },
                { label: "UK Singles Chart", tag: "Top-10 crossover" },
                { label: "Bubbling Under Hot 100", tag: "Emerging break" },
              ].map((t) => (
                <div
                  key={t.label}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                >
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                    {t.tag}
                  </div>
                  <div className="mt-2 font-display font-semibold text-white/95 text-sm md:text-base leading-tight">
                    {t.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

